import React, { FC, useEffect, useRef, useState } from 'react';
import { Mode, State } from '../../services/zodiacEngine/types';
import styles from './AIThinking.module.css';

interface AIThinkingProps {
  isVisible: boolean;
  mode: Mode;
  state: State;
  isDeepThinking?: boolean; // Keeping parameter for backward compatibility
  centered?: boolean; // New prop for centered modal style
  noOffset?: boolean; // Remove margin offset (for tutorial layout where board is centered)
}

// Minimum time the modal stays up after first appearing. Stops it from
// flashing past the player on fast AIs (CreatureAI Medium finishes in well
// under a second, so the original modal was effectively invisible).
const MIN_VISIBLE_MS = 800;

const AIThinking: FC<AIThinkingProps> = ({ isVisible, mode, state, isDeepThinking = false, centered = false, noOffset = false }) => {
  // Only use JS-based dots for the corner version (non-centered)
  const [dots, setDots] = useState('.');

  // Hold the modal open for MIN_VISIBLE_MS after isVisible becomes true, even
  // if the AI finishes its move sooner than that. Without this the modal
  // routinely flashes for <100ms on fast AIs and the user sees nothing.
  const [holding, setHolding] = useState(false);
  const showStartedAtRef = useRef<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isVisible) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      showStartedAtRef.current = Date.now();
      setHolding(true);
      return;
    }
    // isVisible just turned false. Keep showing until MIN_VISIBLE_MS elapsed.
    if (!holding) return;
    const elapsed = showStartedAtRef.current ? Date.now() - showStartedAtRef.current : MIN_VISIBLE_MS;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    hideTimerRef.current = setTimeout(() => {
      setHolding(false);
    }, remaining);
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [isVisible, holding, mode, state]);

  const shown = isVisible || holding;

  useEffect(() => {
    if (!shown || centered) return;

    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '.' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, [shown, centered]);

  if (!shown || (mode !== Mode.AI && mode !== Mode.TestAI && mode !== Mode.Watch) || state !== State.Playing) {
    return null;
  }

  // Centered modal style (for ChallengeAI) - Celestial design with CSS animation
  if (centered) {
    return (
      <div className={`${styles.overlay} ${noOffset ? styles.tutorialOverlay : ''}`}>
        <div className={styles.modal}>
          <span className={styles.title}>
            AI Thinking
            <span className={styles.dots}>
              <span className={styles.dot}>.</span>
              <span className={styles.dot}>.</span>
              <span className={styles.dot}>.</span>
            </span>
          </span>
        </div>
      </div>
    );
  }

  // Original corner style (for BoardContainer)
  return (
    <div className={styles.cornerContainer}>
      AI thinking{dots}
    </div>
  );
};

export default AIThinking;
